import React, { useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import CountryList from "../CountryList";
import { appleApi } from '../../services/api';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { FaSearch, FaChartPie, FaChartBar, FaGamepad, FaStar, FaExternalLinkAlt } from 'react-icons/fa';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const CompetitorAnalysis = () => {
    const [keyword, setKeyword] = useState('');
    const [country, setCountry] = useState('');
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSearch = async () => {
        setLoading(true);
        setError("");
        setResults([]);

        try {
            const { data: result } = await appleApi.get("search/", {
                params: { keyword, country },
            });
            const processedResults = (result?.results ?? []).map((app) => ({
                ...app,
            }));
            setResults(processedResults);
        } catch (err) {
            setError("An error occurred while fetching data. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const scoreData = {
        labels: results.map((app) => app.trackName),
        datasets: [
            {
                label: "Score",
                data: results.map((app) =>
                    app.averageUserRating !== "N/A" ? app.averageUserRating : 0
                ),
                backgroundColor: "rgba(59, 130, 246, 0.7)", // Tailwind blue-500
                borderRadius: 4,
            },
        ],
    };

    const scoreOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Competitor App Scores' },
        },
        scales: {
            y: { beginAtZero: true, max: 5 }
        }
    };

    const categoryData = {
        labels: Array.from(new Set(results.map((app) => app.primaryGenreName))),
        datasets: [
            {
                label: "App Categories",
                data: Array.from(
                    new Set(results.map((app) => app.primaryGenreName))
                ).map(
                    (category) =>
                        results.filter((app) => app.primaryGenreName === category).length
                ),
                backgroundColor: "rgba(16, 185, 129, 0.7)", // Tailwind emerald-500
                borderRadius: 4,
            },
        ],
    };

    const categoryOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'App Category Distribution' },
        },
    };

    const doughnutData = {
        labels: ["High Score (>4)", "Low Score (<=4)"],
        datasets: [
            {
                label: "Score Distribution",
                data: [
                    results.filter((app) => app.averageUserRating > 4).length,
                    results.filter((app) => app.averageUserRating <= 4).length,
                ],
                backgroundColor: [
                    "rgba(59, 130, 246, 0.8)",
                    "rgba(239, 68, 68, 0.8)",
                ],
                borderWidth: 0,
            },
        ],
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
            title: { display: true, text: 'Score Distribution' },
        },
        cutout: '70%',
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                    <FaChartPie className="mr-3 text-blue-600" />
                    Competitor Analysis
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1">App Name / Keyword</label>
                        <input
                            type="text"
                            placeholder="Enter App Name"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        />
                    </div>
                    <div className="md:col-span-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
                    </div>
                    <div className="md:col-span-2">
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                            ) : (
                                <FaSearch className="mr-2" />
                            )}
                            {loading ? "Searching..." : "Search"}
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                 <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-r-md">
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {results.length > 0 && (
                <div className="space-y-8">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <FaGamepad className="mr-2 text-purple-500" />
                            Search Results
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {results.map((app) => (
                                <div key={app.appId || app.trackId} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col">
                                    <div className="p-5 flex flex-col items-center text-center flex-grow">
                                        <img
                                            src={app.artworkUrl60}
                                            alt={app.trackName}
                                            className="w-20 h-20 rounded-2xl shadow-md mb-4 object-cover"
                                        />
                                        <h4 className="font-bold text-gray-800 line-clamp-2 mb-1" title={app.trackName}>{app.trackName}</h4>
                                        <p className="text-xs text-gray-500 mb-3 line-clamp-1">{app.artistName}</p>
                                        
                                        <div className="flex items-center justify-center space-x-2 mb-4">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                                {app.primaryGenreName}
                                            </span>
                                            <span className="flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                                                <FaStar className="mr-1 text-yellow-500" size={10} />
                                                {app.averageUserRating?.toFixed(1)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 text-center">
                                        <a
                                            href={app.trackViewUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center transition-colors"
                                        >
                                            View on App Store <FaExternalLinkAlt className="ml-1" size={10} />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                             <FaChartBar className="mr-2 text-green-500" />
                             Analytics & Insights
                        </h3>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <div className="h-80">
                                    <Bar data={scoreData} options={scoreOptions} />
                                </div>
                            </div>
                            
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <div className="h-64">
                                        <Bar data={categoryData} options={categoryOptions} />
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <div className="h-64 relative">
                                        <Doughnut data={doughnutData} options={doughnutOptions} />
                                    </div>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompetitorAnalysis;
